/* ============================================================
   Accessibility (ADA/WCAG 2.1 AA) remediation shim
   Added July 2026. Fixes issues flagged by axe/WAVE/PowerMapper that
   originate in theme JS (sliders) or third-party app embeds:
   1. aria-hidden slides containing focusable links (announcement bar,
      hero slideshow, promo grid) and the PhotoSwipe lightbox ->
      tabindex="-1" while hidden, restored when shown
   2. Photoswipe zoom button missing accessible name
   3. Boost price-slider handles, pagination buttons + search submit
      button missing names
   4. Also-bought app: aria-label on <a> without href (generic role) ->
      add role="link"; strip role="none"/"presentation" from labeled links
   5. iframes missing title attributes (YouTube embeds etc.)
   6. Shopping Agent widget (open shadow DOM): unnamed role=button,
      aria-label on generic divs, nested interactive controls
   7. Judge.me summary stars: role="img" wrapper contains a link ->
      role="group" (allows interactive children, keeps the label)
   8. Shopify Forms teaser (shadow DOM): nested role=button inside a
      button -> demote nested control to a plain element
   9. Blog tutorial material-list widget (fff-): images without alt,
      checkboxes without labels; li elements in non-list containers ->
      role="list" on the container
   ============================================================ */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';
  var SLIDE_SELECTOR = '.announcement-slider__slide, .slideshow__slide, .hero__slide-link, .promo-grid__slide-link, .pswp';

  function neutralize(container) {
    var els = [];
    if (container.matches && container.matches(FOCUSABLE)) els.push(container);
    els = els.concat(Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE)));
    els.forEach(function (el) {
      if (el.getAttribute('tabindex') !== '-1') {
        if (!el.hasAttribute('data-a11y-prev-tabindex')) {
          el.setAttribute('data-a11y-prev-tabindex', el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : '');
        }
        el.setAttribute('tabindex', '-1');
      }
    });
  }

  function restore(el) {
    var prev = el.getAttribute('data-a11y-prev-tabindex');
    if (prev === '' || prev === null) { el.removeAttribute('tabindex'); } else { el.setAttribute('tabindex', prev); }
    el.removeAttribute('data-a11y-prev-tabindex');
  }

  function fixSlides() {
    document.querySelectorAll(SLIDE_SELECTOR).forEach(function (slide) {
      if (slide.getAttribute('aria-hidden') === 'true') neutralize(slide);
    });
    document.querySelectorAll('[data-a11y-prev-tabindex]').forEach(function (el) {
      if (!el.closest('[aria-hidden="true"]')) restore(el);
    });
  }

  function fixAgentWidget() {
    var host = document.getElementById('ads-agent-host');
    if (!host) return;
    var roots = [host];
    if (host.shadowRoot) roots.push(host.shadowRoot);
    roots.forEach(function (root) {
      root.querySelectorAll('[role="button"]').forEach(function (b) {
        if (b.querySelector('a[href], button, input, select, textarea, [tabindex]')) {
          b.removeAttribute('role');
          b.removeAttribute('tabindex');
          if (b.tagName !== 'BUTTON' && b.tagName !== 'A') b.removeAttribute('aria-label');
        } else if (!b.getAttribute('aria-label') && !b.textContent.trim()) {
          b.setAttribute('aria-label', 'Shopping assistant');
        }
      });
      root.querySelectorAll('div[aria-label]:not([role]), span[aria-label]:not([role])').forEach(function (d) {
        d.removeAttribute('aria-label');
      });
    });
  }

  function fixShadowEmbeds() {
    document.querySelectorAll('[id^="app-embed-container-"]').forEach(function (host) {
      var sr = host.shadowRoot;
      if (!sr) return;
      sr.querySelectorAll('button [role="button"], button a[href], button button, a[href] [role="button"]').forEach(function (x) {
        x.removeAttribute('role');
        x.removeAttribute('tabindex');
        x.removeAttribute('aria-label');
      });
    });
  }

  function fixMaterialList() {
    document.querySelectorAll('input.fff-check').forEach(function (cb) {
      var li = cb.closest('li');
      var name = '';
      if (li) {
        var span = li.querySelector('span');
        if (span) name = span.textContent.trim();
      }
      if (!cb.getAttribute('aria-label')) {
        cb.setAttribute('aria-label', name ? 'Select ' + name : 'Select material');
      }
      if (li) {
        li.querySelectorAll('img:not([alt])').forEach(function (img) {
          img.setAttribute('alt', name || '');
        });
      }
    });
    document.querySelectorAll('li').forEach(function (li) {
      var p = li.parentElement;
      if (p && p.tagName !== 'UL' && p.tagName !== 'OL' && p.tagName !== 'MENU' && !p.getAttribute('role')) {
        p.setAttribute('role', 'list');
      }
    });
  }

  function fixMisc() {
    document.querySelectorAll('.js-photoswipe__zoom:not([aria-label])').forEach(function (b) {
      b.setAttribute('aria-label', 'Zoom product image');
    });
    document.querySelectorAll('.noUi-handle-lower[role="slider"]:not([aria-label])').forEach(function (h) {
      h.setAttribute('aria-label', 'Minimum price');
    });
    document.querySelectorAll('.noUi-handle-upper[role="slider"]:not([aria-label])').forEach(function (h) {
      h.setAttribute('aria-label', 'Maximum price');
    });
    document.querySelectorAll('.boost-sd__button--as-search:not([aria-label])').forEach(function (b) {
      if (!b.textContent.trim()) b.setAttribute('aria-label', 'Search');
    });
    document.querySelectorAll('.boost-sd__pagination-button:not([aria-label])').forEach(function (b) {
      if (b.textContent.trim()) return;
      var cls = b.className || '';
      var label = /next/i.test(cls) ? 'Next page' : (/prev/i.test(cls) ? 'Previous page' : 'Pagination');
      b.setAttribute('aria-label', label);
    });
    document.querySelectorAll('a[role="none"][aria-label], a[role="presentation"][aria-label]').forEach(function (a) {
      a.removeAttribute('role');
    });
    document.querySelectorAll('a[aria-label]:not([href]):not([role])').forEach(function (a) {
      a.setAttribute('role', 'link');
    });
    document.querySelectorAll('.jdgm-rev-widg__summary-stars[role="img"]').forEach(function (s) {
      if (s.querySelector('a[href], button, [tabindex]')) s.setAttribute('role', 'group');
    });
    document.querySelectorAll('iframe:not([title])').forEach(function (f) {
      var src = f.getAttribute('src') || '';
      var t = /youtube|youtu\.be/.test(src) ? 'YouTube video player' : (/vimeo/.test(src) ? 'Vimeo video player' : 'Embedded content');
      f.setAttribute('title', t);
    });
    fixAgentWidget();
    fixShadowEmbeds();
    fixMaterialList();
  }

  var scheduled = false;
  function run() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () {
      scheduled = false;
      try { fixSlides(); fixMisc(); } catch (e) { /* never break the page */ }
    }, 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  window.addEventListener('load', run);
  setInterval(run, 2500);

  var mo = new MutationObserver(run);
  mo.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['aria-hidden']
  });
})();
